<template>
  <basic-form
    name="savingsWidgetForm"
    :validator="v"
    @submit="$emit('submit')"
  >
    <div class="savingsWidget__content">
      <div>
        <div
          class="body-2"
          v-text="$t('savingsWidget.upload.description')"
        />

        <basic-link
          :label="$t('savingsWidget.upload.descriptionLink')"
          @click="openDialog('supportInsurances')"
        >
          <template #trailing>
            <the-icon
              art="regular"
              name="circle-info"
            />
          </template>
        </basic-link>
      </div>

      <div>
        <file-pond
          :accepted-file-types="ACCEPTED_FILE_TYPES"
          :disabled="false"
          :invalid="v.$invalid && v.file.$error"
          :max-files="MAX_FILES"
          :max-file-size="MAX_FILE_SIZE"
          :number-of-existing-files="$props.selectedFile ? 1 : 0"
          :supporting-text="$t('savingsWidget.upload.supportingText')"
          @upload="selectFile"
        />

        <div class="upload__hint caption-regular">
          <span v-html="$t('savingsWidget.upload.supportingText')" />
        </div>
      </div>

      <basic-message
        v-if="v.$invalid && v.file.$error"
        :severity="NOTIFICATION.ERROR"
        :title-label="$t('savingsWidget.upload.error')"
        :closeable="false"
      />

      <basic-list
        button-icon="trash-can-xmark"
        :button-label="$t('screen.personalData.documents.remove')"
        :entries="$props.selectedFile ? [{name: $props.selectedFile.name }] : []"
        @button-click="deleteFile"
      />

      <div>
        <basic-link
          :label="$t('savingsWidget.upload.agb.linkText')"
          @click="openDialog('agb')"
        >
          <template #trailing>
            <the-icon
              art="solid"
              name="arrow-right-long"
            />
          </template>
        </basic-link>

        <basic-checkbox
          v-model="formData.agb"
          name="agb"
          :label="$t('savingsWidget.upload.agb.label')"
          :v="v.agb"
        />
      </div>
    </div>

    <div class="savingsWidget__footer">
      <basic-button
        type="submit"
        hierarchy="primary"
        :label="$t('form.next')"
      />

      <basic-button
        hierarchy="tertiary"
        :label="$t('savingsWidget.upload.manualDataEntry')"
        @click="$emit('open-modal', { mode: 'manual' })"
      >
        <template #leading>
          <the-icon
            art="solid"
            name="pencil"
          />
        </template>
      </basic-button>
    </div>
  </basic-form>

  <modal.container
    id="savingsWidgetModal"
    ref="savingsWidgetModalRef"
  >
    <modal.step
      v-if="modalView === 'agb'"
      id="agb"
      size="midi"
      :step-title="$t('savingsWidget.upload.agb.linkText')"
    >
      <div
        class="body-2"
        v-html="$t('savingsWidget.upload.agb.text')"
      />
    </modal.step>

    <modal.step
      v-else-if="modalView === 'supportInsurances'"
      id="agb"
      size="midi"
      :step-title="$t('savingsWidget.upload.insurers.title')"
    >
      <div class="policy-upload-modal__policy">
        <ul class="body-2">
          <template
            v-for="(insurer, index) in insurers"
            :key="index"
          >
            <li v-html="insurer.label" />
          </template>
        </ul>
      </div>
    </modal.step>
  </modal.container>
</template>

<script setup>
  import { reactive, ref, useTemplateRef } from 'vue'
  import useVuelidate from '@vuelidate/core'
  import { required } from '@vuelidate/validators'

  import FilePond from '@shared/components/Basic/File/FilePond'
  import {
    BasicButton,
    BasicCheckbox,
    BasicForm,
    BasicLink,
    BasicList,
    BasicMessage,
  } from '@shared/components/Basic'
  import Modal from '@shared/components/Basic/ModalButBetter'

  import { NOTIFICATION } from '@shared/config/constants'

  // INIT
  const emit = defineEmits(['drop-file', 'open-modal', 'select-file', 'submit'])
  defineProps({
    insurers: {
      type: Array,
      required: true
    },
    selectedFile: {
      type: Object,
      default: () => null
    }
  })

  // DATA
  const ACCEPTED_FILE_TYPES = 'image/jpeg,image/png,application/pdf'
  const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
  const MAX_FILES = 1

  const formData = reactive({
    agb: null,
    file: null
  })
  const modalView = ref(null)
  const savingsWidgetModalRef = useTemplateRef('savingsWidgetModalRef')

  // METHODS
  function deleteFile() {
    formData.file = null
    emit('drop-file')
  }

  function openDialog(view) {
    modalView.value = view
    savingsWidgetModalRef.value.open()
  }

  function selectFile([file]) {
    formData.file = file

    emit('select-file', file)
  }

  // VALIDATION
  const v = useVuelidate({
    agb: {
      required
    },

    file: {
      required
    }
  }, formData)
</script>

<style scoped>
  .savingsWidget__content {
    display: flex;
    flex-direction: column;
    gap: var(--dotcom-responsive-spacing-res-m);
  }

  .savingsWidget__footer {
    margin: var(--dotcom-responsive-spacing-res-xs) 0 0 0;
    display: flex;
    flex-direction: column;
    gap: var(--fixed-spacing-fix-04);
  }

  .policy-upload-modal__policy li {
    text-transform: capitalize;
  }

  .upload__hint {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    padding: 0 0 0 var(--fixed-spacing-fix-04);
  }
</style>

<style scoped>
  @media (--v-medium) {
    .savingsWidget__footer {
      flex-direction: row-reverse;
      justify-content: space-between;
    }
  }
</style>